import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Printer, Loader2, Globe, Building2, ShieldCheck, Zap } from 'lucide-react';
import aarogyaLogo from '../assets/Aarogya india hindia updated logo.svg';
import { useTheme } from '../context/ThemeContext';

function PurchaseInvoice() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { currentTheme } = useTheme();
    const isDark = currentTheme.textColor === '#FFFFFF';
    
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrder = () => {
            const existingOrders = JSON.parse(localStorage.getItem('mock_purchase_orders') || '[]');
            const localOrder = existingOrders.find(o => o.id === parseInt(id));

            if (localOrder) {
                setOrder({
                    billNumber: localOrder.invoiceNumber,
                    billDate: localOrder.dateTime.split(',')[0],
                    companyName: localOrder.companyName,
                    gstNumber: localOrder.gstin,
                    purchaseItems: localOrder.purchaseItems || [
                        { productName: 'Shilajit 30-Gold 40 Cap.', batchNumber: 'B-34567', mrp: 6200, quantity: 50, discount: 10, gst: 18 },
                        { productName: 'Aloe Vera Nectar', batchNumber: 'B-34567', mrp: 450, quantity: 140, discount: 10, gst: 18 }
                    ]
                });
            } else {
                const mockBills = [
                    { id: 1, billNo: '#BILL-2023-001', company: 'Modern Traders', gstin: '27AAAAA0000A1Z5', date: 'Oct 24, 2023', amount: '45,250.00' },
                    { id: 2, billNo: '#BILL-2023-002', company: 'Aarogya Pharma', gstin: '09BBBBB1111B2Z6', date: 'Oct 25, 2023', amount: '1,12,800.00' },
                    { id: 3, billNo: '#BILL-2023-003', company: 'Global Logistics', gstin: '33CCCCC2222C3Z7', date: 'Oct 26, 2023', amount: '8,400.00' }
                ];

                const staticBill = mockBills.find(b => b.id === parseInt(id));
                if (staticBill) {
                    setOrder({
                        billNumber: staticBill.billNo,
                        billDate: staticBill.date,
                        companyName: staticBill.company,
                        gstNumber: staticBill.gstin,
                        purchaseItems: [
                            { productName: 'Generic Product A', batchNumber: 'B-999', mrp: parseFloat(staticBill.amount.replace(/,/g, '')) / 10, quantity: 10, discount: 0, gst: 18 },
                        ]
                    });
                } else {
                    setOrder({ billNumber: 'INV-XXXXX', billDate: 'Today', companyName: 'Unknown Supplier', gstNumber: 'N/A', purchaseItems: [] });
                }
            }
            setLoading(false);
        };
        fetchOrder();
    }, [id]);

    const calculateItemTotal = (item) => {
        const subtotal = item.mrp * item.quantity;
        const discountAmount = (subtotal * item.discount) / 100;
        const afterDiscount = subtotal - discountAmount;
        const gstAmount = (afterDiscount * item.gst) / 100;
        return afterDiscount + gstAmount;
    };

    const calculateSubtotal = () => order.purchaseItems.reduce((sum, item) => sum + (item.mrp * item.quantity), 0);
    const calculateTotalDiscount = () => order.purchaseItems.reduce((sum, item) => sum + ((item.mrp * item.quantity * item.discount) / 100), 0);
    const calculateTotalTax = () => order.purchaseItems.reduce((sum, item) => {
        const itemSubtotal = item.mrp * item.quantity;
        const discountAmount = (itemSubtotal * item.discount) / 100;
        return sum + ((itemSubtotal - discountAmount) * item.gst) / 100;
    }, 0);

    useEffect(() => {
        if (!loading && order) {
            const isDownload = new URLSearchParams(window.location.search).get('download') === 'true';
            if (isDownload) setTimeout(() => window.print(), 500);
        }
    }, [loading, order]);

    if (loading || !order) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0F172A]' : 'bg-white'}`}>
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: currentTheme.color }} />
            </div>
        );
    }

    const subtotal = calculateSubtotal();
    const tax = calculateTotalTax();
    const discount = calculateTotalDiscount();
    const total = subtotal - discount + tax;

    return (
        <div className={`min-h-screen font-sans transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
            <style>{`
                @media print {
                  .no-print { display: none !important; }
                  body { background: white !important; margin: 0; padding: 0; }
                  .invoice-card { box-shadow: none !important; border: none !important; width: 100% !important; max-width: none !important; color: #000 !important; }
                  .invoice-card * { color: #000 !important; border-color: #eee !important; }
                }
            `}</style>

            <div className="max-w-5xl mx-auto pt-8 px-6 no-print">
                <div className={`flex items-center justify-between mb-10 p-6 rounded-[32px] border shadow-2xl ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                    <button onClick={() => navigate(-1)} className={`p-4 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-white' : 'bg-white text-gray-700'}`}>
                        <ArrowLeft size={24} style={{ color: currentTheme.color }} />
                    </button>
                    <button onClick={() => window.print()} className="flex items-center gap-3 px-8 py-4 rounded-2xl text-white font-black uppercase tracking-[0.2em] text-[10px] shadow-2xl active:scale-95 transition-all" style={{ backgroundColor: currentTheme.color }}>
                        <Printer size={18} />
                        <span>Print Acquisition Bill</span>
                    </button>
                </div>
            </div>

            <div className={`invoice-card max-w-5xl mx-auto mb-20 p-16 rounded-[48px] shadow-2xl border relative overflow-hidden transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                
                {/* Decorative bar */}
                <div className="absolute top-0 left-0 w-full h-3" style={{ backgroundColor: currentTheme.color }}></div>

                <div className="flex justify-between items-start mb-16 relative z-10">
                    <div>
                        <img src={aarogyaLogo} alt="Logo" className={`h-20 mb-6 ${isDark ? 'brightness-200' : ''}`} />
                        <h1 className={`text-3xl font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-[#1E4D43]'}`}>CodeXcorn</h1>
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 mt-2">Logistics Center • Sector 70, Noida</p>
                    </div>
                    <div className="text-right">
                        <div className={`inline-block px-10 py-4 rounded-3xl font-black text-2xl mb-8 tracking-[0.2em] shadow-2xl ${isDark ? 'bg-white/5 text-white border border-white/10' : 'bg-gray-900 text-white'}`}>
                           PURCHASE BILL
                        </div>
                        <div className="space-y-3">
                            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">MANIFEST: <span className={isDark ? 'text-gray-300' : 'text-gray-900'}>#{order.billNumber}</span></p>
                            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">TIMESTAMP: <span className={isDark ? 'text-gray-300' : 'text-gray-900'}>{order.billDate}</span></p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-16 mb-16 relative z-10">
                    <div className={`p-8 rounded-[40px] border ${isDark ? 'bg-white/2 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                        <h3 className="text-[10px] font-black uppercase tracking-[0.3em] mb-6" style={{ color: currentTheme.color }}>Supplier Entity</h3>
                        <p className={`text-2xl font-black mb-2 ${isDark ? 'text-white' : 'text-[#1E4D43]'}`}>{order.companyName}</p>
                        <div className="flex items-center gap-3 text-gray-500 font-black text-[10px] uppercase tracking-widest">
                            <ShieldCheck size={14} style={{ color: currentTheme.color }} />
                            <span>TAX ID: {order.gstNumber}</span>
                        </div>
                    </div>
                    <div className="text-right p-8">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.3em] mb-6" style={{ color: currentTheme.color }}>Consignment Hub</h3>
                        <p className={`text-xl font-black mb-2 ${isDark ? 'text-white' : 'text-[#1E4D43]'}`}>CodeXcorn Warehouse</p>
                        <p className="text-xs font-black text-gray-500 uppercase tracking-widest">Sector 70, Noida, UP • HQ-01</p>
                    </div>
                </div>

                <table className="w-full mb-16 relative z-10">
                    <thead>
                        <tr className={`border-b-2 ${isDark ? 'border-white/10' : 'border-gray-900'}`}>
                            <th className="py-6 text-left text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">Material Spec</th>
                            <th className="py-6 text-center text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">Batch</th>
                            <th className="py-6 text-center text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">Rate</th>
                            <th className="py-6 text-center text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">Units</th>
                            <th className="py-6 text-right text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">Aggregate</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {order.purchaseItems.map((item, idx) => (
                            <tr key={idx} className="group">
                                <td className="py-8">
                                    <p className={`text-lg font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-[#1E4D43]'}`}>{item.productName}</p>
                                </td>
                                <td className="py-8 text-center">
                                    <span className={`text-[10px] font-black px-3 py-1 rounded-lg uppercase tracking-widest ${isDark ? 'bg-white/5 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>{item.batchNumber}</span>
                                </td>
                                <td className={`py-8 text-center text-sm font-black tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>₹{item.mrp.toLocaleString()}</td>
                                <td className={`py-8 text-center text-sm font-black tabular-nums ${isDark ? 'text-white' : 'text-[#1E4D43]'}`}>{item.quantity}</td>
                                <td className="py-8 text-right text-lg font-black tabular-nums" style={{ color: currentTheme.color }}>₹{calculateItemTotal(item).toLocaleString('en-IN')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="flex justify-end relative z-10">
                    <div className={`w-full max-w-sm p-8 rounded-[40px] space-y-6 ${isDark ? 'bg-white/2' : 'bg-gray-50 shadow-inner'}`}>
                        <div className="flex justify-between items-center text-[10px] font-black text-gray-500 uppercase tracking-widest">
                            <span>Manifest Subtotal</span>
                            <span className={isDark ? 'text-white' : 'text-[#1E4D43]'}>₹{subtotal.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center text-[10px] font-black text-gray-500 uppercase tracking-widest">
                            <span>System Levy (18%)</span>
                            <span className={isDark ? 'text-white' : 'text-[#1E4D43]'}>₹{Math.round(tax).toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center text-[10px] font-black text-gray-500 uppercase tracking-widest pb-6 border-b border-white/10">
                            <span>Incentive Rebate</span>
                            <span className="text-red-500">-₹{discount.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                            <span className="text-[11px] font-black uppercase tracking-[0.2em]" style={{ color: currentTheme.color }}>Net Aggregate</span>
                            <span className={`text-4xl font-black tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>₹{total.toLocaleString('en-IN')}</span>
                        </div>
                    </div>
                </div>

                <div className="mt-24 flex justify-between items-end relative z-10 opacity-30">
                    <div className="text-[9px] font-black text-gray-500 uppercase tracking-[0.5em]">
                        <p>Authenticated via Indhara CRM Ecosystem</p>
                        <p>Manifest Date: {new Date().toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                        <div className={`w-48 h-1 ${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded-full mb-4`}></div>
                        <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>Authorized Signatory</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PurchaseInvoice;
